import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-10-02 09:40:33 +0200 (Fri, 02 Oct 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.juenger $
//  Revision  : $Rev: 166189 $
//  Purpose   : custom buttons
// =============================================================================
// ---Third-party imports
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { KeyboardShortCut, Button } from 'uix/components';
import { WikiModalPortal } from 'uix/wiki/components';
import { Sel } from 'uix/registries';
import { useSelector } from 'uix/hooks';
import { useWikiContext } from 'uix/wiki/utils';
// ---Local imports
import S from './modal.css';
// ---Globals
/** sets inert (inactive) on everything outside el, returns the elements to release later */ const inertOutside = (el)=>{
    let inerted = [];
    for(let node = el; node.parentElement && node !== document.body; node = node.parentElement){
        for (let sibling of node.parentElement.children){
            if (sibling !== node && !sibling.hasAttribute('inert')) {
                sibling.setAttribute('inert', '');
                inerted.push(sibling);
            }
        }
    }
    return inerted;
};
// ---Exports
export const Modal = ({ caption, hideCaption, children, imageId, onClose, open, scrolling, controls, width = 1600, style, backgroundColor, backgroundOpacity, contentColor, padding, fullwidth = false, className })=>{
    let apiurl = useSelector(Sel.apiurl);
    let { mobile } = useWikiContext();
    let dialogRef = useRef();
    let bgStyle = {};
    useEffect(()=>{
        // note: modal is only mounted while open - inert background keeps Tab inside the modal
        let opener = document.activeElement;
        let inerted = inertOutside(dialogRef.current);
        dialogRef.current.focus();
        return ()=>{
            inerted.forEach((e)=>e.removeAttribute('inert'));
            opener?.focus?.();
        };
    }, []);
    let percentToDecimal = (percent)=>parseFloat(percent) / 100;
    let modalStyles = {
        ...contentColor && {
            '--presentation_themeColor_content': contentColor
        },
        ...padding && {
            '--presentation_modal_padding': padding
        },
        ...backgroundColor && {
            '--presentation_themeColor_backdrop': backgroundColor
        },
        ...backgroundOpacity && {
            '--presentation_modal_bgOpacity': percentToDecimal(backgroundOpacity)
        }
    };
    if (imageId) {
        let imgurl = `/image/pagebox/${imageId}/${width}`;
        bgStyle = {
            backgroundImage: `url(${apiurl.href(imgurl)})`
        };
    }
    let controlButtons = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            controls,
            /*#__PURE__*/ _jsx(Button, {
                className: S.controlButtons,
                bordered: true,
                style: {
                    margin: 'auto',
                    zIndex: 30
                },
                icon: "close",
                onClick: onClose
            })
        ]
    });
    let topEls = /*#__PURE__*/ _jsx(_Fragment, {
        children: controlButtons
    });
    let bottomEls = !hideCaption ? /*#__PURE__*/ _jsx(_Fragment, {
        children: caption
    }) : null;
    return /*#__PURE__*/ _jsx(WikiModalPortal, {
        children: /*#__PURE__*/ _jsxs("div", {
            ref: dialogRef,
            role: "dialog",
            "aria-modal": "true",
            tabIndex: -1,
            className: clsx(open && S.modal, scrolling && S.scrolling, imageId && S.bgActive, mobile && S.mobile, !topEls && S.noTop, className),
            style: {
                ...modalStyles,
                ...style
            },
            onClick: (e)=>e.stopPropagation(),
            onPointerDown: (e)=>e.stopPropagation(),
            onPointerMove: (e)=>e.stopPropagation(),
            onKeyDown: (e)=>{
                e.stopPropagation();
                if (e.key === 'Escape') onClose();
            },
            open: true,
            children: [
                /*#__PURE__*/ _jsx(KeyboardShortCut, {
                    name: "Escape",
                    onTrigger: onClose
                }),
                topEls && /*#__PURE__*/ _jsx("div", {
                    className: S.topBar,
                    children: topEls
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: S.bgColor
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: S.bgImg,
                    style: bgStyle
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: clsx(S.contentCnt, caption && S.bottom, !bottomEls && S.noBottom),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: clsx(S.content, fullwidth && S.fullwidth),
                        children: children
                    })
                }),
                bottomEls && /*#__PURE__*/ _jsx("div", {
                    className: S.bottomBar,
                    children: bottomEls
                })
            ]
        })
    });
};
